import React from 'react';
import { Truck, Zap, Globe, Package, ArrowRight, MessageCircle, Mail, Phone } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const ShippingAndExchange22 = () => {
    const shippingOptions = [
        {
            icon: Truck,
            title: 'Standard Shipping',
            time: '5-7 Business Days',
            cost: 'Free on orders over \u20B92,999',
            description: 'Reliable delivery across India via our trusted logistics partners. Orders under \u20B92,999 are charged a flat \u20B9149 shipping fee. Each piece is carefully wrapped in eco-friendly packaging to protect the handcraft.'
        },
        {
            icon: Zap,
            title: 'Express Shipping',
            time: '2-3 Business Days',
            cost: '\u20B9499',
            description: 'Priority dispatch and expedited delivery for when you need your handcrafted piece sooner. Available for most metro cities and tier-1 locations across India.'
        },
        {
            icon: Globe,
            title: 'International Shipping',
            time: '7-14 Business Days',
            cost: 'Varies by country',
            description: 'We ship Indian handcraft worldwide. Shipping costs are calculated at checkout based on destination and package weight. Customs duties may apply depending on your country\'s import regulations.'
        }
    ];

    const exchangeSteps = [
        {
            step: 1,
            title: 'Request',
            description: 'Initiate an exchange request within 7 days of delivery through your account dashboard or by contacting our support team via WhatsApp or email with your order number.'
        },
        {
            step: 2,
            title: 'Approval',
            description: 'Our team will review your request within 24-48 hours and send you a confirmation email with return shipping instructions and a prepaid label.'
        },
        {
            step: 3,
            title: 'Ship Back',
            description: 'Pack the item securely in its original packaging with all tags and authenticity certificate intact. Drop it off at the nearest courier pickup point or schedule a reverse pickup.'
        },
        {
            step: 4,
            title: 'Receive New',
            description: 'Once we receive and inspect the returned item, your replacement will be dispatched within 2-3 business days. You will receive tracking details via email and SMS.'
        }
    ];

    const importantNotes = [
        'Exchange requests must be raised within 7 days of delivery.',
        'Items must be unused, unwashed, and in their original condition with all tags and labels attached.',
        'Original packaging and authenticity certificate must be intact and included with the return.',
        'Custom-made, bespoke, or made-to-order garments are not eligible for exchange due to their personalised nature.',
        'Slight colour and texture variations in handcrafted products are natural and not grounds for exchange.',
        'Intimate wear, accessories, and items marked as "Final Sale" cannot be exchanged.',
        'Exchanges are subject to availability of the requested size or colour.',
        'First exchange on any order is free. Subsequent exchanges may incur a \u20B9149 shipping fee.',
        'Refunds are processed within 7-10 business days of receiving the returned product.',
        'For COD orders, refunds are processed via bank transfer to your provided account details.',
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                {/* Hero */}
                <section style={{
                    padding: '120px 24px 60px', borderBottom: '1px solid #E8DDD0',
                    textAlign: 'center',
                }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                        <p style={{
                            fontSize: '12px', fontWeight: 500, color: '#C4572A',
                            letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '16px',
                        }}>
                            Handcrafted with Care
                        </p>
                        <h1 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: 400,
                            color: '#5C3D2E', marginBottom: '16px',
                        }}>
                            Shipping & Exchange
                        </h1>
                        <p style={{ fontSize: '16px', color: '#8B7355', lineHeight: 1.6 }}>
                            Each piece is handcrafted by skilled artisans. We take extra care in packaging and shipping to ensure your treasure arrives safely.
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '60px 24px 80px' }}>

                    {/* Handcrafted Messaging */}
                    <div style={{
                        backgroundColor: '#F3ECE4', borderRadius: '12px', padding: '28px 32px',
                        marginBottom: '48px', textAlign: 'center', borderLeft: '4px solid #C4572A',
                    }}>
                        <p style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: '22px', fontWeight: 500, color: '#5C3D2E', margin: '0 0 8px 0',
                        }}>
                            "Each piece is handcrafted, not mass-produced"
                        </p>
                        <p style={{ fontSize: '14px', color: '#8B7355', margin: 0, lineHeight: 1.6 }}>
                            Please allow additional time for handcrafted and made-to-order items. Your patience supports artisan livelihoods and preserves traditional craft techniques.
                        </p>
                    </div>

                    {/* Shipping Options */}
                    <h2 style={{
                        fontFamily: "'Cormorant Infant', Georgia, serif",
                        fontSize: '32px', fontWeight: 500, color: '#5C3D2E',
                        marginBottom: '28px', textAlign: 'center',
                    }}>
                        Shipping Options
                    </h2>

                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px', marginBottom: '64px',
                    }}>
                        {shippingOptions.map((option, i) => (
                            <div key={i} style={{
                                backgroundColor: '#FFFFFF', borderRadius: '12px',
                                border: '1px solid #E8DDD0', padding: '32px',
                                transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(92,61,46,0.1)';
                                e.currentTarget.style.transform = 'translateY(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.transform = 'none';
                            }}
                            >
                                <div style={{
                                    width: '52px', height: '52px', borderRadius: '50%',
                                    backgroundColor: '#FFF0EB', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', marginBottom: '20px',
                                }}>
                                    <option.icon size={24} color="#C4572A" />
                                </div>
                                <h3 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '22px', fontWeight: 600, color: '#5C3D2E',
                                    margin: '0 0 8px 0',
                                }}>
                                    {option.title}
                                </h3>
                                <div style={{
                                    display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap',
                                }}>
                                    <span style={{
                                        padding: '4px 12px', borderRadius: '20px',
                                        backgroundColor: '#F3ECE4', fontSize: '13px',
                                        fontWeight: 600, color: '#5C3D2E',
                                    }}>
                                        {option.time}
                                    </span>
                                    <span style={{
                                        padding: '4px 12px', borderRadius: '20px',
                                        backgroundColor: '#FFF0EB', fontSize: '13px',
                                        fontWeight: 600, color: '#C4572A',
                                    }}>
                                        {option.cost}
                                    </span>
                                </div>
                                <p style={{ fontSize: '14px', color: '#8B7355', lineHeight: 1.6, margin: 0 }}>
                                    {option.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Exchange Process */}
                    <h2 style={{
                        fontFamily: "'Cormorant Infant', Georgia, serif",
                        fontSize: '32px', fontWeight: 500, color: '#5C3D2E',
                        marginBottom: '28px', textAlign: 'center',
                    }}>
                        Exchange Process
                    </h2>

                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '24px', marginBottom: '64px',
                    }}>
                        {exchangeSteps.map((item) => (
                            <div key={item.step} style={{
                                backgroundColor: '#FFFFFF', borderRadius: '12px',
                                border: '1px solid #E8DDD0', padding: '28px',
                                position: 'relative',
                            }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '50%',
                                    backgroundColor: '#C4572A', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
                                }}>
                                    <span style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 700 }}>
                                        {item.step}
                                    </span>
                                </div>
                                <h3 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '20px', fontWeight: 600, color: '#5C3D2E',
                                    margin: '0 0 10px 0',
                                }}>
                                    {item.title}
                                </h3>
                                <p style={{ fontSize: '14px', color: '#8B7355', lineHeight: 1.6, margin: 0 }}>
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Important Notes */}
                    <div style={{
                        backgroundColor: '#FFFFFF', borderRadius: '12px',
                        border: '1px solid #E8DDD0', padding: '32px', marginBottom: '48px',
                    }}>
                        <h3 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: '24px', fontWeight: 600, color: '#5C3D2E',
                            margin: '0 0 20px 0',
                        }}>
                            Important Notes
                        </h3>
                        <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none' }}>
                            {importantNotes.map((note, i) => (
                                <li key={i} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                                    padding: '10px 0',
                                    borderBottom: i < importantNotes.length - 1 ? '1px solid #E8DDD0' : 'none',
                                }}>
                                    <div style={{
                                        width: '6px', height: '6px', borderRadius: '50%',
                                        backgroundColor: '#C4572A', flexShrink: 0, marginTop: '7px',
                                    }} />
                                    <p style={{ fontSize: '14px', color: '#5C3D2E', lineHeight: 1.6, margin: 0, opacity: 0.85 }}>
                                        {note}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact for Help */}
                    <div style={{
                        backgroundColor: '#F3ECE4', borderRadius: '12px',
                        padding: '32px', textAlign: 'center',
                    }}>
                        <h3 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: '26px', fontWeight: 600, color: '#5C3D2E',
                            margin: '0 0 12px 0',
                        }}>
                            Need Help?
                        </h3>
                        <p style={{ fontSize: '14px', color: '#8B7355', marginBottom: '24px', lineHeight: 1.6 }}>
                            Our craft consultants are here to assist with any shipping or exchange queries.
                        </p>
                        <div style={{
                            display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap',
                        }}>
                            <a
                                href="https://wa.me/919876543210"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '12px 24px', backgroundColor: '#25D366',
                                    color: '#FFFFFF', borderRadius: '8px', fontSize: '14px',
                                    fontWeight: 600, textDecoration: 'none',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                }}
                            >
                                <MessageCircle size={18} /> WhatsApp
                            </a>
                            <a
                                href="mailto:support@maati.in"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '12px 24px', backgroundColor: '#C4572A',
                                    color: '#FFFFFF', borderRadius: '8px', fontSize: '14px',
                                    fontWeight: 600, textDecoration: 'none',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                }}
                            >
                                <Mail size={18} /> Email Us
                            </a>
                            <a
                                href="tel:+919876543210"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '12px 24px', backgroundColor: '#5C3D2E',
                                    color: '#FFFFFF', borderRadius: '8px', fontSize: '14px',
                                    fontWeight: 600, textDecoration: 'none',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                }}
                            >
                                <Phone size={18} /> Call Us
                            </a>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default ShippingAndExchange22;
